// start.ng integration through the documented hidden connect page (https://start.ng/docs.md):
//   startng:whoami      -> startng:user            (player identity, signed in or guest)
//   startng:leaderboard -> startng:leaderboardData (top players)
//   startng:score       -> startng:scoreSaved      (save a finished run, signed-in players only)
// Every reply is checked against the connect frame's origin AND window, and matched by requestId.
// The game keeps working when start.ng does not answer (local play, offline, blocked frames).

export const START_NG = 'https://start.ng';
const FRAME_LOAD_TIMEOUT = 8000;

let framePromise = null;

function connectFrame() {
  framePromise ??= new Promise((resolve, reject) => {
    const el = document.createElement('iframe');
    el.src = `${START_NG}/connect`;
    el.hidden = true;
    el.title = 'start.ng connect';
    el.setAttribute('aria-hidden', 'true');
    const timer = setTimeout(() => reject(new Error('connect frame timed out')), FRAME_LOAD_TIMEOUT);
    el.onload = () => { clearTimeout(timer); resolve(el); };
    el.onerror = (e) => { clearTimeout(timer); reject(e); };
    document.body.append(el);
  }).catch((err) => {
    framePromise = null; // allow a later retry
    throw err;
  });
  return framePromise;
}

/**
 * Sends a message and resolves to start.ng's reply, or null if it didn't answer in time.
 * Read-only requests (`resend: true`) are re-posted with the same requestId until answered, which
 * covers a request posted while the connect page was still starting up. Writes are sent once.
 */
export async function askStartNg(message, replyType, timeoutMs = 5000, { resend = false } = {}) {
  let el;
  try {
    el = await connectFrame();
  } catch {
    return null;
  }
  return new Promise((resolve) => {
    const requestId = Math.random().toString(36).slice(2) + Date.now().toString(36);
    let again = 0;
    const done = (reply) => { clearTimeout(timer); clearInterval(again); removeEventListener('message', onMessage); resolve(reply); };
    const timer = setTimeout(() => done(null), timeoutMs);
    function onMessage(e) {
      if (e.origin !== START_NG || e.source !== el.contentWindow) return;
      if (e.data?.type === replyType && e.data.requestId === requestId) done(e.data);
    }
    const post = () => {
      try {
        el.contentWindow.postMessage({ ...message, requestId }, START_NG);
      } catch {
        done(null);
      }
    };
    addEventListener('message', onMessage);
    post();
    if (resend) again = setInterval(post, 800);
  });
}

/** Absolute sign-in URL that returns the player to this page. */
export function signInUrl() {
  return `${START_NG}/?return_to=${encodeURIComponent(location.origin + location.pathname)}`;
}

/** Sign-in only works when the game is served over https (i.e. live on *.start.ng). */
export function canSignIn() {
  return location.protocol === 'https:';
}

export function isStartNgHost() {
  return /(^|\.)start\.ng$/.test(location.hostname);
}

/** The homepage opens the game with ?guest when the player chose to play as a guest. */
export function chosenGuest() {
  return new URLSearchParams(location.search).has('guest');
}

/** We use the connect page, so a returned #startng_session is not needed: keep it out of the address bar. */
export function cleanSessionFromUrl() {
  if (location.hash.includes('startng_session=')) {
    try { history.replaceState(null, '', location.pathname + location.search); } catch { /* ignore */ }
  }
}

/**
 * Player identity state with change notifications.
 * status: 'loading' | 'user' | 'guest' | 'offline'
 */
export class StartNgPlayer {
  constructor() {
    this.status = 'loading';
    this.user = null;
    this.guest = null;
    this.listeners = new Set();
    this.inflight = null;
    this.lastRefresh = 0;
  }

  onChange(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }

  emit() { for (const fn of this.listeners) fn(this); }

  /** The profile to display: signed-in user, start.ng's guest, or a local fallback. */
  get profile() {
    if (this.user) return { name: this.user.name, account: this.user.account ? `@${this.user.account}` : '', face: this.user.face, signedIn: true };
    if (this.guest) return { name: this.guest.name || 'Guest', account: 'Not signed in', face: this.guest.face, signedIn: false };
    return { name: 'Guest', account: this.status === 'loading' ? 'Connecting to start.ng…' : 'Playing offline', face: null, signedIn: false };
  }

  refresh({ force = false } = {}) {
    if (this.inflight) return this.inflight;
    if (!force && Date.now() - this.lastRefresh < 1500) return Promise.resolve(this);
    this.inflight = (async () => {
      const reply = await askStartNg({ type: 'startng:whoami' }, 'startng:user', 6000, { resend: true });
      this.lastRefresh = Date.now();
      if (!reply) {
        // keep a previously known identity if a later refresh simply times out
        if (!this.user && !this.guest) this.status = 'offline';
      } else {
        this.user = reply.user ?? null;
        this.guest = reply.guest ?? null;
        this.status = this.user ? 'user' : 'guest';
      }
      this.inflight = null;
      this.emit();
      return this;
    })();
    return this.inflight;
  }

  /** Re-ask when the player returns to the tab: they may have signed in or out on start.ng. */
  watch() {
    const again = () => { if (document.visibilityState === 'visible') this.refresh(); };
    document.addEventListener('visibilitychange', again);
    window.addEventListener('focus', again);
    return () => { document.removeEventListener('visibilitychange', again); window.removeEventListener('focus', again); };
  }
}

/** Read the shared board. Returns { ok: true, entries } | { ok: false, error }. */
export async function fetchLeaderboard(limit = 10) {
  const reply = await askStartNg({ type: 'startng:leaderboard', limit }, 'startng:leaderboardData', 7000, { resend: true });
  if (!reply) return { ok: false, error: 'start.ng did not answer. The leaderboard is available when the game is played on start.ng.', offline: true };
  if (!reply.ok) return { ok: false, error: reply.error || 'The leaderboard could not be loaded.' };
  return { ok: true, entries: Array.isArray(reply.entries) ? reply.entries : [] };
}

/**
 * Save a finished run. `stats` must contain exactly the keys declared in startng.json.
 * Returns { ok: true, result } | { ok: false, error, offline? } - never claims success without a reply.
 */
export async function submitRun(stats) {
  const reply = await askStartNg({ type: 'startng:score', stats }, 'startng:scoreSaved', 9000);
  if (!reply) return { ok: false, error: 'start.ng did not answer, so the score was not saved.', offline: true };
  if (!reply.ok) return { ok: false, error: reply.error || 'The score was not saved.' };
  return { ok: true, result: reply.result || null };
}
